import React from 'react';
import { useDataFetching } from '../../hooks/useDataFetching';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import StatCard from '../shared/StatCard';
import { formatRelativeTime } from '../../utils/formatters';
import { FileText } from 'lucide-react';

const EventLogs = () => {
  const { data: logs, loading, error, refetch } = useDataFetching(
    async () => {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/event-logs`);
      if (!response.ok) throw new Error('Failed to fetch event logs');
      return response.json();
    },
    []
  );

  if (loading) return <LoadingState message="Loading event logs..." />;
  if (error) return <ErrorState message={error} onRetry={refetch} />;

  const errorCount = logs?.filter(l => l.level === 'ERROR').length || 0;
  const warnCount = logs?.filter(l => l.level === 'WARN').length || 0;
  const totalEvents = logs?.reduce((sum, l) => sum + l.count, 0) || 0;
  const avgNoiseScore = logs?.length
    ? (logs.reduce((sum, l) => sum + l.noise_score, 0) / logs.length * 100).toFixed(0)
    : 0;

  const getLevelColor = (level) => {
    if (level === 'ERROR') return 'bg-red-100 text-red-800';
    if (level === 'WARN') return 'bg-yellow-100 text-yellow-800';
    if (level === 'INFO') return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getNoiseColor = (score) => {
    if (score > 0.7) return 'text-red-600';
    if (score > 0.4) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Event Logs</h1>
          <p className="mt-2 text-gray-600">
            Monitor and analyze event logs from all services
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Events"
            value={totalEvents}
            subtitle="Last hour"
          />
          <StatCard
            title="Errors"
            value={errorCount}
            subtitle="Requiring attention"
          />
          <StatCard
            title="Warnings"
            value={warnCount}
            subtitle="Medium priority"
          />
          <StatCard
            title="Avg Noise Score"
            value={`${avgNoiseScore}%`}
            subtitle="Filter effectiveness"
          />
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Events</h2>
            <p className="text-sm text-gray-600 mt-1">Real-time event stream from monitored services</p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Count
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Noise Score
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs && logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatRelativeTime(log.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FileText className="h-4 w-4 text-gray-400 mr-2" />
                        <div className="text-sm text-gray-900">{log.service}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getLevelColor(log.level)}`}>
                        {log.level}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-md">{log.message}</div>
                      <div className="text-xs text-gray-500 mt-1">{log.source}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.count.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${getNoiseColor(log.noise_score)}`}>
                        {(log.noise_score * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button className="text-blue-600 hover:text-blue-900 mr-3">
                        View
                      </button>
                      <button className="text-gray-600 hover:text-gray-900">
                        Filter
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventLogs;